import React, { useEffect, useMemo, useState, useCallback } from "react";
import Referral from "../components/Referral";
import { useNavigate } from "react-router-dom";
import { Bell, Search, Trash2, CheckCheck, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import confetti from "canvas-confetti";
import AdInterstitial from "../components/AdInterstitial";
import ScratchCard from "../components/ScratchCard";
import CustomerLayout from "../components/CustomerLayout";
import { LiveBoard } from "../components/LiveBoard";
import { useAuth } from "../context/AuthContext";
import { PAGE, BANNERS, EmptyState, TxTable, NotifList, LuckyBoard, TokensGrid } from "../components/customer/walletBits";
import { DashboardPanel, WalletPanel, RedeemPanel, ProfilePanel, HelpPanel } from "../components/customer/walletPanels";

export default function CustomerWallet() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [bal, setBal] = useState(null);
  const [txns, setTxns] = useState([]);
  const [reds, setReds] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [luckyTickets, setLuckyTickets] = useState([]);
  const [luckyStats, setLuckyStats] = useState(null);
  const [draws, setDraws] = useState({ upcoming: [], recent_winners: [] });
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const [activeToken, setActiveToken] = useState(null);
  const [mobile, setMobile] = useState("");
  const [redeem, setRedeem] = useState({ amount_rupees: "", method: "UPI", upi: "", account_holder: "", account_number: "", ifsc: "", bank_name: "" });
  const [busy, setBusy] = useState(false);
  const [txFilter, setTxFilter] = useState("All");
  const [txSearch, setTxSearch] = useState("");
  const [txPage, setTxPage] = useState(1);
  const [walPage, setWalPage] = useState(1);
  const [tokenPage, setTokenPage] = useState(1);
  const [luckyPage, setLuckyPage] = useState(1);
  const [notifPage, setNotifPage] = useState(1);
  const [delOpen, setDelOpen] = useState(false);
  const [slide, setSlide] = useState(0);

  const load = useCallback(async () => {
    api.get("/wallet").then((r) => setBal(r.data)).catch(() => {});
    api.get("/wallet/transactions").then((r) => setTxns(r.data.transactions)).catch(() => {});
    api.get("/wallet/redemptions").then((r) => setReds(r.data.redemptions)).catch(() => {});
    api.get("/wallet/tokens").then((r) => setTokens(r.data.tokens)).catch(() => {});
    api.get("/wallet/ledger").then((r) => setLedger(r.data.entries)).catch(() => {});
    api.get("/wallet/lucky-tickets").then((r) => { setLuckyTickets(r.data.tickets); setLuckyStats(r.data.stats); }).catch(() => {});
    api.get("/wallet/draws").then((r) => setDraws(r.data)).catch(() => {});
    api.get("/wallet/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/wallet/notifications").then((r) => { setNotifs(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => setSlide((s) => (s + 1) % BANNERS.length), 4500); return () => clearInterval(id); }, []);
  const unscratchedCount = tokens.filter((t) => !t.scratched).length;
  const sortedTokens = [...tokens].sort((a, b) => (a.scratched === b.scratched ? 0 : a.scratched ? 1 : -1));

  const onNav = (key) => { if (key === "notifications" && unread > 0) markRead(); setActive(key); };

  const onCardRevealed = (paise, resultType) => {
    if (resultType === "LUCKY_TICKET") {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#10b981", "#34d399", "#fbbf24", "#f9fafb"] });
    } else if (paise > 0) {
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 }, colors: ["#10b981", "#34d399", "#fbbf24", "#f9fafb"] });
    }
  };
  const closeCard = async (revealed = true) => { setActiveToken(null); await load(); if (revealed) setAdOpen(true); };

  const linkMobile = async () => {
    if (!mobile) return;
    setBusy(true);
    try { await api.post("/wallet/link-mobile", { mobile }); toast.success("Mobile linked; cashback merged"); await load(); }
    catch { toast.error("Could not link mobile. Please try again."); } finally { setBusy(false); }
  };

  const doRedeem = async () => {
    setBusy(true);
    try {
      const payload = { amount_rupees: Number(redeem.amount_rupees), method: redeem.method };
      if (redeem.method === "UPI") payload.dest = redeem.upi;
      else payload.bank = { account_holder: redeem.account_holder, account_number: redeem.account_number, ifsc: redeem.ifsc, bank_name: redeem.bank_name };
      await api.post("/wallet/redeem", payload);
      toast.success("Redemption submitted");
      setAdOpen(true);
      setRedeem({ amount_rupees: "", method: "UPI", upi: "", account_holder: "", account_number: "", ifsc: "", bank_name: "" });
      await load(); setActive("wallet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Unable to process redemption. Please try again."); } finally { setBusy(false); }
  };

  const markRead = async () => { try { await api.post("/wallet/notifications/read"); setUnread(0); } catch (e) { console.warn("mark notifications read failed", e); } };

  const deleteAccount = async () => {
    setBusy(true);
    try { await api.post("/wallet/account/delete"); toast.success("Your account has been deleted"); await logout(); navigate("/"); }
    catch { toast.error("Could not delete account. Please try again."); setBusy(false); }
  };

  const totalDisplay = bal?.total_display ?? (bal ? `₹${(((bal.available_paise || 0) + (bal.pending_paise || 0)) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "₹0.00");
  const availPaise = bal?.available_paise || 0;
  const minRedeem = cfg?.min_redeem_paise ?? 10000;
  const canRedeem = availPaise >= minRedeem;

  const allTx = useMemo(() => {
    const a = [];
    txns.forEach((t) => a.push({ id: t.txn_id, type: "Cashback", amount: t.amount_paise, sign: 1, status: t.status, date: t.created_at, sub: t.merchant_id || "" }));
    reds.forEach((r) => a.push({ id: r.redemption_id, type: "Redemption", amount: r.amount_paise, sign: -1, status: r.status, date: r.created_at, sub: r.method || "" }));
    tokens.filter((t) => t.scratched).forEach((t) => a.push({ id: t.tokenId, type: "Token", amount: t.cashback_paise, sign: 1, status: "USED", date: t.created_at, sub: t.merchant_id || "" }));
    return a.sort((x, y) => new Date(y.date) - new Date(x.date));
  }, [txns, reds, tokens]);

  const filteredTx = useMemo(() => allTx.filter((t) =>
    (txFilter === "All" || t.type === txFilter) &&
    (!txSearch || `${t.id} ${t.sub} ${t.amount / 100} ${t.status}`.toLowerCase().includes(txSearch.toLowerCase()))
  ), [allTx, txFilter, txSearch]);
  const txPages = Math.max(1, Math.ceil(filteredTx.length / PAGE));
  const txItems = filteredTx.slice((txPage - 1) * PAGE, txPage * PAGE);
  useEffect(() => { setTxPage(1); }, [txFilter, txSearch]);

  const walPages = Math.max(1, Math.ceil(ledger.length / PAGE));
  const walItems = ledger.slice((walPage - 1) * PAGE, walPage * PAGE);
  const tokenPages = Math.max(1, Math.ceil(sortedTokens.length / PAGE));
  const tokenItems = sortedTokens.slice((tokenPage - 1) * PAGE, tokenPage * PAGE);
  const luckyPages = Math.max(1, Math.ceil(luckyTickets.length / PAGE));
  const luckyItems = luckyTickets.slice((luckyPage - 1) * PAGE, luckyPage * PAGE);
  const notifPages = Math.max(1, Math.ceil(notifs.length / PAGE));
  const notifItems = notifs.slice((notifPage - 1) * PAGE, notifPage * PAGE);

  return (
    <CustomerLayout active={active} onNav={onNav} unread={unread}>
      {active === "referral" && <Referral />}
      {active === "live" && <LiveBoard />}
      <AdInterstitial open={adOpen} onClose={() => setAdOpen(false)} />
      {activeToken && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/90 p-5" data-testid="scratch-modal">
          <div className="w-full max-w-sm rounded-3xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <ScratchCard token={activeToken} onReveal={onCardRevealed} onDone={closeCard} />
          </div>
        </div>
      )}

      {/* ============ DASHBOARD ============ */}
      {active === "dashboard" && (
        <DashboardPanel user={user} slide={slide} setSlide={setSlide} setActive={setActive} bal={bal} unscratchedCount={unscratchedCount} allTx={allTx} mobile={mobile} setMobile={setMobile} linkMobile={linkMobile} busy={busy} />
      )}

      {/* ============ TOKENS ============ */}
      {active === "tokens" && (
        <TokensGrid tokens={tokens} tokenItems={tokenItems} tokenPage={tokenPage} tokenPages={tokenPages} setTokenPage={setTokenPage} setActiveToken={setActiveToken} />
      )}

      {/* ============ MY LUCKY TICKETS ============ */}
      {active === "lucky" && (
        <LuckyBoard draws={draws} luckyStats={luckyStats} luckyTickets={luckyTickets} luckyItems={luckyItems} luckyPage={luckyPage} luckyPages={luckyPages} setLuckyPage={setLuckyPage} />
      )}

      {/* ============ WALLET ============ */}
      {active === "wallet" && (
        <WalletPanel bal={bal} user={user} totalDisplay={totalDisplay} setActive={setActive} canRedeem={canRedeem} cfg={cfg} ledger={ledger} walItems={walItems} walPage={walPage} walPages={walPages} setWalPage={setWalPage} />
      )}

      {/* ============ REDEEM ============ */}
      {active === "redeem" && (
        <RedeemPanel bal={bal} cfg={cfg} canRedeem={canRedeem} redeem={redeem} setRedeem={setRedeem} doRedeem={doRedeem} busy={busy} reds={reds} />
      )}

      {/* ============ TRANSACTIONS ============ */}
      {active === "transactions" && (
        <div className="space-y-4" data-testid="transactions-page">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {["All", "Cashback", "Redemption", "Token"].map((f) => (
                <button key={f} data-testid={`tx-filter-${f}`} onClick={() => setTxFilter(f)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${txFilter === f ? "bg-emerald-500 text-slate-950" : "border border-slate-700 text-slate-400 hover:text-white"}`}>{f}</button>
              ))}
            </div>
            <div className="relative sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input data-testid="tx-search" placeholder="Search transactions" value={txSearch} onChange={(e) => setTxSearch(e.target.value)} className="w-full rounded-full border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>
          <TxTable txItems={txItems} filteredTx={filteredTx} txPage={txPage} txPages={txPages} setTxPage={setTxPage} />
        </div>
      )}

      {/* ============ NOTIFICATIONS ============ */}
      {active === "notifications" && (
        <div className="space-y-4" data-testid="notifications-page">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Bell className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Notifications</h2></div>
            {notifs.length > 0 && <button data-testid="mark-all-read" onClick={markRead} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:text-white"><CheckCheck className="h-3.5 w-3.5" /> Mark all as read</button>}
          </div>
          {notifs.length === 0 ? (
            <EmptyState icon={Inbox} testid="no-notifications" title="You're all caught up." sub="Notifications about your cashback and redemptions will appear here." />
          ) : (
            <NotifList notifItems={notifItems} notifPage={notifPage} notifPages={notifPages} setNotifPage={setNotifPage} />
          )}
        </div>
      )}

      {/* ============ PROFILE ============ */}
      {active === "profile" && <ProfilePanel user={user} setDelOpen={setDelOpen} />}

      {/* ============ HELP ============ */}
      {active === "help" && <HelpPanel />}

      {/* delete confirmation */}
      {delOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/90 p-5" data-testid="delete-account-modal">
          <div className="w-full max-w-sm rounded-3xl border border-rose-500/30 bg-slate-900 p-6 shadow-2xl">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-rose-500/15 text-rose-400"><Trash2 className="h-6 w-6" /></div>
            <h3 className="mt-4 font-heading text-lg font-bold">Delete your account?</h3>
            <p className="mt-2 text-sm text-slate-400">This permanently removes your personal information and signs you out. This action cannot be undone.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setDelOpen(false)} className="flex-1 rounded-full border border-slate-700 py-2.5 text-sm font-semibold text-slate-200">Cancel</button>
              <button data-testid="confirm-delete-account" onClick={deleteAccount} disabled={busy} className="flex-1 rounded-full bg-rose-500 py-2.5 text-sm font-semibold text-white hover:bg-rose-400 disabled:opacity-50">{busy ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
}
